import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import {
  DiagnosticReport,
  Observation,
  ServiceRequest,
} from "@curai/js-fhir-tools";
import type { LabTestWorkflowStatus } from "./useLabTests";

interface ReportResult {
  diagnosticReport: DiagnosticReport;
  observations: Observation[];
}

interface OrderResultHistoryItem {
  testCode: string;
  serviceRequest: ServiceRequest;
  workflowStatus: LabTestWorkflowStatus;
  reportResults: ReportResult[];
}

interface OrderResultHistoryResponse {
  orderResultHistory: OrderResultHistoryItem[];
}

export const useOrderResultHistory = ({
  patientId,
  testCode,
}: {
  patientId: string;
  testCode: string;
}) => {
  return useApi<OrderResultHistoryResponse>({
    queryOptions: {
      enabled: Boolean(testCode),
      queryKey: QueryKeys.Patient.MedicalRecords.Labs.ORDER_RESULT_HISTORY(
        patientId,
        testCode,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/labs/results/order-results-history",
          {
            params: {
              patientId,
              serviceRequestCode: testCode,
            },
          },
        );
        return response?.data;
      },
      select: (data) => {
        return {
          orderResultHistory: data.orderResultHistory.map((item) => ({
            testCode: item.testCode,
            serviceRequest: new ServiceRequest(item.serviceRequest),
            workflowStatus: item.workflowStatus,
            reportResults: item.reportResults.map((report) => ({
              diagnosticReport: new DiagnosticReport(report.diagnosticReport),
              observations: report.observations.map((observation) => {
                return new Observation(observation);
              }),
            })),
          })),
        };
      },
    },
  });
};
